import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo } from "react"

import { ProductType } from "@/api/shared"
import {
  type DenormalizedWorkspace,
  type Workspace,
  getViewDataWorkspaces,
} from "@/api/workspaces"
import { queryKeys } from "@/queries/keys"
import { unwrapOrThrow } from "@/util/result"

// Workspaces are seeded into the query cache from bootstrap viewData by
// `hydrateQueriesFromViewData` (non-slim) or by the deferred fetcher behind the
// `DeferredViewDataGate` (slim). Both populate `queryKeys.workspaces.all` before
// any consumer mounts, so the hydrated data is authoritative and mutations keep
// the cache current instead of refetching on every mount. The queryFn is only a
// fallback for render paths that mount without hydrated data.
const EMPTY_WORKSPACES: readonly Workspace[] = []

/**
 * Flattens the denormalized workspace (nested `lists`) into the normalized
 * `Workspace` shape (`listIds`) that consumers expect. `listIds` is never read
 * for rendering — the `lists` domain owns per-workspace list state — but we
 * keep the field so the type matches the pre-migration Redux selector output.
 */
export function toWorkspace(workspace: DenormalizedWorkspace): Workspace {
  const { lists, ...rest } = workspace
  return { ...rest, listIds: (lists ?? []).map((list) => list.id) }
}

function useWorkspacesData(): readonly Workspace[] {
  const { data } = useQuery({
    queryKey: queryKeys.workspaces.all,
    async queryFn() {
      return unwrapOrThrow(await getViewDataWorkspaces()).map(toWorkspace)
    },
    staleTime: Infinity,
  })
  return data ?? EMPTY_WORKSPACES
}

const useAllWorkspacesOfAllProducts = (): readonly Workspace[] => useWorkspacesData()

export const useWorkspaceById = (id: number): Workspace | undefined => {
  const workspaces = useWorkspacesData()
  return workspaces.find((ws) => ws.id === id)
}

export const useAllWorkspacesOfProductType = (productType: ProductType): Workspace[] => {
  const workspaces = useAllWorkspacesOfAllProducts()
  // Memoized because callers pass the result into effect dependency arrays and
  // into memoized tree builders; filter always allocates a new array.
  return useMemo(
    () => workspaces.filter((ws) => ws.productType === productType),
    [workspaces, productType],
  )
}

export const useAllWorkspacesWorkspaces = (): Workspace[] =>
  useAllWorkspacesOfProductType(ProductType.Workspaces)

export const useAllFileFeedsWorkspaces = (): Workspace[] =>
  useAllWorkspacesOfProductType(ProductType.FileFeeds)

export const useAllWorkflowsWorkspaces = (): Workspace[] =>
  useAllWorkspacesOfProductType(ProductType.Workflows)

export const useOrgDefaultWorkspaceWorkspace = (): Workspace | undefined => {
  const allWorkspaces = useAllWorkspacesWorkspaces()
  return allWorkspaces.find((ws) => ws.isPublic && ws.isDefault && !ws.creator)
}

export const useUserDefaultWorkspaceWorkspace = (
  userId: number,
): Workspace | undefined => {
  const allWorkspaces = useAllWorkspacesWorkspaces()
  return allWorkspaces.find(
    (ws) => ws.isDefault && ws.creator && ws.creator.id === userId,
  )
}

/**
 * Cache writers for workspace mutations. Mutation call sites update the query
 * cache directly so the shared `workspaces` query stays current without a
 * refetch.
 *
 * Deleting a workspace still dispatches `deleteWorkspaceById` at the call site
 * so the `lists` Redux slice can cascade-remove that workspace's lists; that
 * domain has not been migrated to React Query yet.
 */
export function useWorkspacesCache() {
  const queryClient = useQueryClient()

  const upsertWorkspace = (workspace: DenormalizedWorkspace) => {
    const normalized = toWorkspace(workspace)
    queryClient.setQueryData<Workspace[]>(queryKeys.workspaces.all, (previous) => {
      const workspaces = previous ?? []
      const index = workspaces.findIndex((ws) => ws.id === normalized.id)
      if (index === -1) {
        return [...workspaces, normalized]
      }
      const next = workspaces.slice()
      next[index] = normalized
      return next
    })
  }

  const removeWorkspace = (id: number) => {
    queryClient.setQueryData<Workspace[]>(queryKeys.workspaces.all, (previous) =>
      (previous ?? []).filter((ws) => ws.id !== id),
    )
  }

  return { upsertWorkspace, removeWorkspace }
}
